/**
 * Écran de signature du contrat (1ʳᵉ connexion après souscription). Bloquant.
 *
 * Procédé : (1) lecture du contrat · le PDF officiel « projet » est affiché en
 * grand, téléchargeable ; (2) signature électronique par code OTP envoyé par
 * SMS au mobile du signataire (n° + horodatage tracés).
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { motion } from "motion/react";
import { useMutation } from "@tanstack/react-query";
import { ApiClientError } from "@victor/api-client";
import { api, getAccessToken } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";
import { PageSpinner } from "../../ui/Spinner";
import { Logo } from "../../ui/Logo";
import { useDemoMode } from "../../lib/demo/demoMode";

export function SignatureContratPage() {
  const { status } = useAuth();
  const demoActive = useDemoMode();

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);
  const [cgvOk, setCgvOk] = useState(false);
  const [cguOk, setCguOk] = useState(false);
  const [step, setStep] = useState<"read" | "otp">("read");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [phoneMasked, setPhoneMasked] = useState<string | null>(null);
  const [sentInfo, setSentInfo] = useState<string | null>(null);
  const [signedId, setSignedId] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Charge le PDF « projet » (avec le token) → blob → iframe.
  useEffect(() => {
    if (status !== "authenticated") return;
    let url: string | null = null;
    let cancelled = false;
    void (async () => {
      try {
        const token = getAccessToken();
        const res = await fetch(api.meContrats.signPreviewPdfUrl(), {
          headers: { Authorization: `Bearer ${token ?? ""}` },
        });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await res.blob();
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch {
        if (!cancelled) setPdfError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [status]);

  const sendCodeMut = useMutation({
    mutationFn: () => api.meContrats.sendSignCode(phone),
    onSuccess: (r) => {
      setError(null);
      setPhoneMasked(r.phoneMasked);
      setSentInfo(
        r.channel === "email"
          ? `Un code à 6 chiffres a été envoyé par e-mail à ${r.emailMasked ?? "votre adresse"}.`
          : `Un code à 6 chiffres a été envoyé au ${r.phoneMasked}.`,
      );
    },
    onError: (e) =>
      setError(e instanceof ApiClientError ? e.message : "Envoi du code impossible."),
  });

  const signMut = useMutation({
    mutationFn: () => api.meContrats.signWithCode(phone, code),
    onSuccess: (c) => {
      sessionStorage.removeItem("victor.blocked_code");
      setSignedId(c.id); // ouvre le pop-up de bienvenue
    },
    onError: (e) =>
      setError(e instanceof ApiClientError ? e.message : "Signature impossible."),
  });

  async function downloadContrat(): Promise<void> {
    if (!signedId) return;
    setDownloading(true);
    try {
      const token = getAccessToken();
      const res = await fetch(api.meContrats.pdfUrl(signedId), {
        headers: { Authorization: `Bearer ${token ?? ""}` },
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Contrat-Victor.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      window.open(api.meContrats.pdfUrl(signedId), "_blank");
    } finally {
      setDownloading(false);
    }
  }

  if (status === "loading") return <PageSpinner />;
  if (status === "anonymous") return <Navigate to="/login" replace />;
  // En mode démo : on n'impose pas la signature, on laisse explorer.
  if (demoActive) return <Navigate to="/" replace />;

  return (
    <div className="flex min-h-screen flex-col bg-navy-50">
      {/* Header corpo */}
      <header className="flex items-center justify-between border-b border-navy-100 bg-white px-5 py-3.5 sm:px-8">
        <Logo size={30} withWordmark />
        <span className="inline-flex items-center gap-2 text-xs font-semibold text-navy-500">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Espace sécurisé
        </span>
      </header>

      {/* Corps plein écran : process (gauche) + PDF pleine hauteur (droite) */}
      <main className="grid flex-1 lg:grid-cols-[minmax(0,460px)_1fr]">
        {/* Colonne process */}
        <section className="order-2 flex flex-col border-t border-navy-100 bg-white px-6 py-8 sm:px-10 lg:order-1 lg:border-t-0">
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col">
            <div>
              {/* Animation de signature (motion : le tracé se dessine en boucle) */}
              <motion.svg
                viewBox="0 0 140 56"
                className="h-12 w-32"
                fill="none"
                aria-hidden="true"
              >
                <line x1="6" y1="48" x2="120" y2="48" stroke="#E2E8F0" strokeWidth="2" strokeLinecap="round" />
                <motion.path
                  d="M6 40 C 16 6, 26 8, 28 34 C 29 49, 35 49, 39 26 C 43 8, 51 12, 53 37 C 54 49, 61 47, 67 28 C 74 8, 85 14, 89 39 C 93 54, 106 49, 116 26"
                  stroke="#2563EB"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{
                    duration: 1.9,
                    ease: "easeInOut",
                    repeat: Infinity,
                    repeatType: "loop",
                    repeatDelay: 1.1,
                  }}
                />
              </motion.svg>

              <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-accent-50 px-3 py-1 text-xs font-semibold text-accent-700 ring-1 ring-accent-100">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                </svg>
                Dernière étape
              </span>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-navy-800">
                Signez votre contrat
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-navy-600">
                Votre paiement est confirmé. Lisez votre contrat de souscription,
                puis signez-le électroniquement par code SMS pour accéder à votre
                espace.
              </p>
            </div>

            {/* Rassurance */}
            <ul className="mt-6 space-y-2.5">
              {[
                "Paiement confirmé · mandat SEPA actif",
                "Signature électronique à valeur légale (code SMS)",
                "Vos données sont hébergées en France",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-navy-700">
                  <svg className="mt-0.5 shrink-0 text-emerald-500" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  {t}
                </li>
              ))}
            </ul>

            {/* Action de signature */}
            <div className="mt-7 space-y-4 rounded-2xl border border-navy-100 bg-navy-50/60 p-5">
          {step === "read" ? (
            <>
              <p className="text-xs leading-relaxed text-navy-600">
                À consulter :{" "}
                <a href="https://myvictor.fr/dpa" target="_blank" rel="noreferrer" className="font-semibold text-accent-700 underline">DPA</a>,{" "}
                <a href="https://myvictor.fr/sla" target="_blank" rel="noreferrer" className="font-semibold text-accent-700 underline">SLA</a> et la{" "}
                <a href="https://myvictor.fr/confidentialite" target="_blank" rel="noreferrer" className="font-semibold text-accent-700 underline">Politique de confidentialité</a>.
              </p>
              <label className="flex cursor-pointer items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={cgvOk}
                  onChange={(e) => setCgvOk(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-navy-300 text-accent-500 focus:ring-accent-300"
                />
                <span className="text-navy-700">
                  J'ai lu et j'accepte les{" "}
                  <a href="https://myvictor.fr/cgv" target="_blank" rel="noreferrer" className="font-semibold text-accent-700 underline">
                    Conditions Générales de Vente (CGV)
                  </a>.
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={cguOk}
                  onChange={(e) => setCguOk(e.target.checked)}
                  className="mt-0.5 h-5 w-5 rounded border-navy-300 text-accent-500 focus:ring-accent-300"
                />
                <span className="text-navy-700">
                  J'ai lu et j'accepte les{" "}
                  <a href="https://myvictor.fr/cgu" target="_blank" rel="noreferrer" className="font-semibold text-accent-700 underline">
                    Conditions Générales d'Utilisation (CGU)
                  </a>.
                </span>
              </label>
              <button
                type="button"
                disabled={!cgvOk || !cguOk}
                onClick={() => {
                  setError(null);
                  setStep("otp");
                }}
                className="w-full rounded-xl bg-navy-800 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-navy-700 disabled:opacity-40"
              >
                Passer à la signature →
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-navy-800">
                  Signature par code SMS
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setStep("read");
                    setError(null);
                  }}
                  className="text-xs font-semibold text-navy-500 underline"
                >
                  ← Relire
                </button>
              </div>

              {!phoneMasked ? (
                <>
                  <label className="block text-sm text-navy-700">
                    Votre numéro de mobile
                    <input
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="06 12 34 56 78"
                      className="mt-1 w-full rounded-lg border border-navy-300 px-3 py-2.5 text-base focus:border-accent-400 focus:ring-2 focus:ring-accent-200 focus:outline-none"
                    />
                  </label>
                  <button
                    type="button"
                    disabled={phone.trim().length < 8 || sendCodeMut.isPending}
                    onClick={() => sendCodeMut.mutate()}
                    className="w-full rounded-xl bg-accent-500 px-6 py-3 text-sm font-bold text-white transition-all hover:bg-accent-600 disabled:opacity-50"
                  >
                    {sendCodeMut.isPending ? "Envoi du code…" : "Recevoir le code par SMS"}
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-navy-600">
                    {sentInfo ?? `Un code à 6 chiffres a été envoyé au ${phoneMasked}.`}
                  </p>
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    placeholder="------"
                    className="w-full rounded-lg border border-navy-300 px-3 py-2.5 text-center text-2xl font-bold tracking-[0.4em] focus:border-accent-400 focus:ring-2 focus:ring-accent-200 focus:outline-none"
                  />
                  <button
                    type="button"
                    disabled={code.length !== 6 || signMut.isPending}
                    onClick={() => signMut.mutate()}
                    className="w-full rounded-xl bg-accent-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-accent-500/25 transition-all hover:bg-accent-600 disabled:opacity-50 disabled:shadow-none"
                  >
                    {signMut.isPending ? "Signature en cours…" : "Signer et accéder à mon espace →"}
                  </button>
                  <button
                    type="button"
                    disabled={sendCodeMut.isPending}
                    onClick={() => {
                      setCode("");
                      sendCodeMut.mutate();
                    }}
                    className="w-full text-xs font-semibold text-accent-700 underline disabled:opacity-50"
                  >
                    Renvoyer un code
                  </button>
                </>
              )}
            </>
          )}

              {error ? (
                <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">
                  {error}
                </p>
              ) : null}
            </div>

            <p className="mt-auto pt-6 text-center text-xs text-navy-500">
              Une question ?{" "}
              <a href="mailto:support@myvictor.fr" className="text-accent-700 underline">
                support@myvictor.fr
              </a>
            </p>
          </div>
        </section>

        {/* Colonne PDF (droite, pleine hauteur) */}
        <section className="order-1 flex min-h-[60vh] flex-col bg-navy-100/50 lg:order-2 lg:min-h-0">
          <div className="flex items-center justify-between border-b border-navy-100 bg-white/70 px-5 py-2.5">
            <span className="text-sm font-bold text-navy-800">
              Contrat de souscription · Victor
            </span>
            {pdfUrl ? (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg border border-navy-200 bg-white px-3 py-1.5 text-xs font-bold text-navy-700 transition-colors hover:bg-navy-50"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Ouvrir / télécharger
              </a>
            ) : null}
          </div>
          <div className="relative flex-1">
            {pdfError ? (
              <div className="flex h-full items-center justify-center p-8 text-center text-sm text-navy-600">
                <span>
                  Le contrat n'a pas pu être affiché.{" "}
                  <a
                    href={api.meContrats.signPreviewPdfUrl()}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-accent-700 underline"
                  >
                    Ouvrir le PDF
                  </a>
                </span>
              </div>
            ) : pdfUrl ? (
              <iframe
                title="Contrat de souscription"
                src={pdfUrl}
                className="absolute inset-0 h-full w-full border-0"
              />
            ) : (
              <div className="flex h-full items-center justify-center">
                <PageSpinner />
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Pop-up de bienvenue après signature */}
      {signedId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h2 className="mt-4 text-2xl font-bold tracking-tight text-navy-800">
              Bienvenue chez Victor !
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-navy-600">
              Votre contrat est signé. Téléchargez-le pour vos archives, puis
              accédez à votre espace.
            </p>
            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={downloadContrat}
                disabled={downloading}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-navy-200 px-6 py-3 text-sm font-bold text-navy-700 transition-colors hover:bg-navy-50 disabled:opacity-50"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {downloading ? "Téléchargement…" : "Télécharger mon contrat (PDF)"}
              </button>
              <button
                type="button"
                onClick={() => window.location.replace("/")}
                className="w-full rounded-xl bg-accent-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-accent-500/25 transition-colors hover:bg-accent-600"
              >
                Accéder à mon tableau de bord →
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
